"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
interface ServiceItem {
  id: string;
  badge: string;
  title: React.ReactNode;
  description: string;
  href: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const services: ServiceItem[] = [{
  id: "batch-cooking",
  badge: "01",
  title: "Batch cooking",
  description: "Des repas préparés chez toi, pour remplir ton frigo et te libérer de la cuisine.",
  href: "/BatchCookingPage",
  image: {
    src: "https://slow-mama.com/wp-content/uploads/2026/10/e9fdb1d1-e63b-41af-b90c-10f3721bd75a.png?v=i7w1vegbp19",
    srcset: "https://slow-mama.com/wp-content/uploads/2026/10/e9fdb1d1-e63b-41af-b90c-10f3721bd75a.png?v=rpfd8mmhrjg-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 33vw",
    alt: "Préparation de repas et batch cooking fait maison"
  }
}, {
  id: "nesting-party",
  badge: "02",
  title: "Nesting Party",
  description: "Un moment convivial à domicile avant la naissance: je réunit tes proches pour préparer le nid ensemble et célébrer l'arrivée de bébé.",
  href: "/NestingPartyPage",
  image: {
    src: "https://slow-mama.com/wp-content/uploads/2026/10/5a40ebbc-186a-4db7-be05-3bbec92e80d2.png?v=lpaijrgi0xd",
    srcset: "/img/nesting_party_gathering_home-wvc-srcset",
    sizes: "/img/nesting_party_gathering_home-wvc-sizes",
    alt: "Une femme enceinte entourée de sa famille et de ses amies dans un salon chaleureux, préparant ensemble la chambre de bébé avant la naissance"
  }
}, {
  id: "maternite",
  badge: "03",
  title: "Accompagnement maternité (6 mois plus)",
  description: "Ton bébé grandit, et tes besoins comptent toujours. Un soutien à domicile pour te donner du temps et alléger le quotidien.",
  href: "/accompagnements#accompagnement-maternite",
  image: {
    src: "https://slow-mama.com/wp-content/uploads/2026/10/2c8c5371-ef3b-446c-a405-0998641a2d6c.png?v=mrkf1xppnms",
    srcset: "https://slow-mama.com/wp-content/uploads/2026/10/2c8c5371-ef3b-446c-a405-0998641a2d6c.png?v=b0gfo5e0i95-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 33vw",
    alt: "Accompagnement maternité avec une maman et son bébé"
  }
}, {
  id: "panier-cadeau",
  badge: "04",
  title: "Panier cadeau postpartum",
  description: "Une attention réconfortante à offrir à une maman après la naissance.",
  href: "/accompagnements#panier-cadeau-postpartum",
  image: {
    src: "https://slow-mama.com/wp-content/uploads/2026/10/2e96f463-e222-4da7-99d2-2d2a002546d5.png?v=rfpqvqi27hn",
    srcset: "https://slow-mama.com/wp-content/uploads/2026/10/2e96f463-e222-4da7-99d2-2d2a002546d5.png?v=ilhs3kszv5a-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 33vw",
    alt: "Panier cadeau postpartum réconfortant pour jeune maman"
  }
}];
export default function PathwayOrientation() {
  return <section id="pathwayorientation" data-nav="light" className="relative overflow-hidden bg-muted text-foreground py-12 md:py-16 lg:py-20">
      {/* Subtle decorative watermark */}
      <div className="pointer-events-none absolute -top-12 -right-12 w-48 h-48 opacity-15 select-none text-primary/25" aria-hidden="true">
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full stroke-current" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M100 20 C100 60, 140 100, 180 100 C140 100, 100 140, 100 180 C100 140, 60 100, 20 100 C60 100, 100 60, 100 20 Z" />
          <path d="M100 50 C100 75, 125 100, 150 100 C125 100, 100 125, 100 150 C100 125, 75 100, 50 100 C75 100, 100 75, 100 50 Z" />
        </svg>
      </div>

      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        {/* Subtle Header Block */}
        <div className="mx-auto max-w-xl text-center mb-12 md:mb-16">
          <span className="inline-block text-xs sm:text-sm uppercase tracking-[0.2em] text-primary/80 font-semibold mb-2">SOUTIEN APRès la naissance et pendant les premiers mois de Bébé</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground">
            D&apos;autres façons de prendre soin de toi.
          </h2>
        </div>

        {/* 4-Item Compact Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {services.map((item, i) => <div key={item.id} data-index={i} className="group flex flex-col justify-between rounded-lg bg-card/40 p-5 sm:p-6 border border-border/50 hover:border-border transition-colors duration-300">
              <div>
                {/* Compact arched image container with overlapping badge */}
                <div className="relative -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4">
                  <div className="aspect-[4/3] sm:aspect-[5/4] lg:aspect-[1/1] overflow-hidden rounded-t-lg rounded-b-none bg-muted">
                    <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                  </div>
                  {/* Numbered badge */}
                  <div className="absolute -bottom-3 left-5 sm:left-6 size-8 rounded-full bg-[#43362F] font-serif text-sm text-[#FCFAF7] shadow-sm flex items-center justify-center select-none" aria-hidden="true">
                    {item.badge}
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-serif text-xl sm:text-2xl lg:text-[28px] text-foreground font-normal leading-snug mb-2 pt-1">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base text-foreground/75 leading-relaxed mb-4">
                  {item.description}
                </p>
              </div>

              {/* Action link */}
              <div className="pt-2 border-t border-border/40">
                <Link to={item.href} className="inline-flex items-center gap-1.5 text-primary tracking-wider text-sm sm:text-base font-semibold transition-colors duration-200 hover:text-primary/80">
                  <span>DÉCOUVRIR</span>
                  <ArrowRight className="size-4 sm:size-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}